(() => {
  const WISHLIST_STORAGE_KEY = 'rwady-wishlist-products';
  const CART_UPDATED_EVENT = 'maximize:cart:updated';
  const interactionUtils = window.RwadyInteractionUtils;
  const activeAnimations = new WeakMap();
  let cartStateRequest = null;
  let cartStateRefreshQueued = false;

  const initializeSearchNormalization = () => {
    const searchApi = window.RwadySearchNormalization;
    if (!searchApi) return;

    const syncClearButton = (input) => {
      const clearButton = input?.closest('form')?.querySelector('[data-rwady-search-clear]');
      if (!clearButton) return;
      const visible = Boolean(input.value.trim());
      clearButton.hidden = !visible;
      clearButton.setAttribute('aria-hidden', String(!visible));
    };

    const currentParams = new URLSearchParams(window.location.search);
    const originalQuery = currentParams.get('rwady_q');

    if (originalQuery !== null) {
      document.querySelectorAll('[data-rwady-search-input]').forEach((input) => {
        input.value = originalQuery;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        syncClearButton(input);
      });
    }

    document.querySelectorAll('[data-rwady-search-input]').forEach(syncClearButton);

    document.addEventListener('input', (event) => {
      if (!event.target.matches('[data-rwady-search-input]')) return;
      syncClearButton(event.target);
    });

    document.addEventListener('click', (event) => {
      const clearButton = event.target.closest('[data-rwady-search-clear]:not([data-rwady-collection-search-clear])');
      if (!clearButton) return;
      const clearInput = clearButton.closest('form')?.querySelector('[data-rwady-search-input]');
      if (!clearInput) return;
      event.preventDefault();
      clearInput.value = '';
      clearInput.dispatchEvent(new Event('input', { bubbles: true }));
      clearInput.focus();
    });

    document.querySelectorAll('#FacetFormAside, #FacetsFormFilter').forEach((form) => {
      if (originalQuery === null) return;
      let originalInput = form.querySelector('input[name="rwady_q"]');
      if (!originalInput) {
        originalInput = document.createElement('input');
        originalInput.type = 'hidden';
        originalInput.name = 'rwady_q';
        form.append(originalInput);
      }
      originalInput.value = originalQuery;
    });

    document.addEventListener('submit', (event) => {
      const form = event.target.closest('form[role="search"]');
      const visibleInput = form?.querySelector('[data-rwady-search-input]');
      if (!visibleInput) return;

      const params = searchApi.createRwadySearchParams(visibleInput.value);
      let normalizedInput = form.querySelector('input[data-rwady-normalized-query]');
      let originalInput = form.querySelector('input[data-rwady-original-query]');

      if (!normalizedInput) {
        normalizedInput = document.createElement('input');
        normalizedInput.type = 'hidden';
        normalizedInput.name = 'q';
        normalizedInput.dataset.rwadyNormalizedQuery = '';
        form.append(normalizedInput);
      }
      if (!originalInput) {
        originalInput = document.createElement('input');
        originalInput.type = 'hidden';
        originalInput.name = 'rwady_q';
        originalInput.dataset.rwadyOriginalQuery = '';
        form.append(originalInput);
      }

      normalizedInput.value = params.q;
      originalInput.value = params.rwady_q;
      visibleInput.removeAttribute('name');
    });
  };

  const initializeCategoryNameSearch = () => {
    const page = document.querySelector('[data-rwady-category-page]');
    const grid = page?.querySelector('[data-category-grid]');
    if (!grid || page.dataset.categorySearchReady === 'true') return;
    const inputs = Array.from(document.querySelectorAll('.header-search [data-rwady-search-input]'));
    if (!inputs.length) return;
    page.dataset.categorySearchReady = 'true';

    const isArabic = page.getAttribute('dir') === 'rtl';
    const normalize = (value) => String(value || '').normalize('NFKC').toLocaleLowerCase()
      .replace(/[\u064B-\u065F\u0670\u0640]/g, '').replace(/[أإآٱ]/g, 'ا')
      .replace(/ى/g, 'ي').replace(/\s+/g, ' ').trim();
    const brandAliases = [
      [/كنوود|كينوود|كنود/g, 'kenwood'], [/بوش/g, 'bosch'],
      [/فيليبس|فيلبس/g, 'philips'], [/تيفال/g, 'tefal'], [/براون/g, 'braun'],
      [/ريمنغتون|رمنغتون|ريمنجتون/g, 'remington'], [/رسل هوبس|راسل هوبس/g, 'russell hobbs'],
      [/بيبي ليس|بيبيليس/g, 'babyliss'], [/بيسيل|بيسل/g, 'bissell'],
      [/سنكور|سينكور/g, 'sencor'], [/ديلونجي|ديلونغي/g, 'delonghi'],
      [/دريمي/g, 'dreame'], [/انكر/g, 'anker'], [/يوفي/g, 'eufy'],
      [/لامارت|لمارت/g, 'lamart'], [/ريفـلون|ريفلون/g, 'revlon'],
    ];
    const normalizeSearch = value => brandAliases.reduce(
      (text, [pattern, brand]) => text.replace(pattern, brand), normalize(window.RwadyBrandAliases?.normalizeQuery(value) ?? value)
    ).replace(/['’]/g, '');
    const readIndex = element => {
      try { return JSON.parse(element.dataset.categoryIndex); } catch (_) { return null; }
    };
    const items = Array.from(grid.children).map((element) => {
      const count = element.querySelector('.rwady-category-page__count');
      const link = element.querySelector('a.rwady-category-page__card');
      return {
        element, count, link, index: readIndex(element),
        isParent: !!element.querySelector('[data-category-parent]'),
        originalCount: count?.textContent || '', originalHref: link?.getAttribute('href'),
        name: normalizeSearch(element.querySelector('.rwady-category-page__name')?.textContent),
        vendors: normalizeSearch(element.dataset.categoryVendors),
      };
    });
    const message = document.createElement('p');
    message.className = 'rwady-category-page__empty';
    message.setAttribute('role', 'status');
    message.setAttribute('aria-live', 'polite');
    message.hidden = true;
    grid.after(message);
    const summary = document.createElement('div');
    summary.className = 'rwady-category-page__search-summary';
    summary.setAttribute('role', 'status');
    summary.setAttribute('aria-live', 'polite');
    summary.hidden = true;
    const summaryText = document.createElement('span');
    const clearSearch = document.createElement('button');
    clearSearch.type = 'button';
    clearSearch.textContent = isArabic ? 'مسح البحث وعرض الكل' : 'Clear search and show all';
    clearSearch.addEventListener('click', () => filter(''));
    summary.append(summaryText, clearSearch);
    grid.before(summary);

    const vendorsIn = node => [...(node?.vendors || []).filter(vendor => vendor.count !== 0),
      ...(node?.children || []).flatMap(vendorsIn)];
    const itemVendors = item => item.isParent ? (item.index?.children || []).flatMap(vendorsIn)
      : (item.index?.vendors || []).filter(vendor => vendor.count !== 0);
    const knownVendors = [...new Set(items.flatMap(item => itemVendors(item).map(vendor => vendor.name)))];
    let revision = 0;
    let activeCounts = 0;
    const countQueue = [];
    const countCache = new Map();
    const drainCounts = () => {
      while (activeCounts < 4 && countQueue.length) {
        const task = countQueue.shift();
        if (task.revision !== revision) { task.reject(new Error('Superseded search')); continue; }
        activeCounts++;
        task.run().then(task.resolve, task.reject).finally(() => { activeCounts--; drainCounts(); });
      }
    };
    const filteredURL = (href, vendors) => {
      const url = new URL(href, window.location.origin);
      url.searchParams.delete('filter.p.vendor');
      vendors.forEach(vendor => url.searchParams.append('filter.p.vendor', vendor));
      return url;
    };
    const loadCount = (href, vendors, currentRevision) => {
      const url = filteredURL(href, vendors);
      url.searchParams.set('view', 'rwady-category-count');
      const key = url.toString();
      if (countCache.has(key)) return Promise.resolve(countCache.get(key));
      return new Promise((resolve, reject) => {
        countQueue.push({ revision: currentRevision, resolve, reject, run: async () => {
          const response = await fetch(key, { credentials: 'same-origin', headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error('Category count unavailable');
          const data = await response.json();
          if (!Number.isInteger(data.count) || data.count < 0 || !Array.isArray(data.vendors)
              || (data.count > 0 && (vendors.length !== data.vendors.length || vendors.some(vendor => !data.vendors.includes(vendor))))) {
            throw new Error('Category vendor filter was not applied');
          }
          countCache.set(key, data.count);
          return data.count;
        }});
        drainCounts();
      });
    };

    const filter = (value) => {
      value = String(value || '');
      const currentURL = new URL(window.location.href);
      if (value.trim()) currentURL.searchParams.set('category_q', value);
      else currentURL.searchParams.delete('category_q');
      if (currentURL.href !== window.location.href) {
        history.replaceState(history.state, '', currentURL.pathname + currentURL.search + currentURL.hash);
      }
      const currentRevision = ++revision;
      const query = normalizeSearch(value);
      const words = query.split(' ').filter(Boolean);
      const selectedVendors = knownVendors.filter(vendor => words.some(word => normalizeSearch(vendor).includes(word)));
      const brandSearch = selectedVendors.length > 0;
      const pendingCounts = [];
      let foundEmpty = false;
      let count = 0;
      items.forEach(item => {
        const { element, name, vendors, index } = item;
        const matchingVendors = itemVendors(item).filter(vendor => selectedVendors.includes(vendor.name)
          && words.every(word => name.includes(word) || normalizeSearch(vendor.name).includes(word)));
        const names = [...new Set(matchingVendors.map(vendor => vendor.name))];
        const matches = brandSearch ? names.length > 0 : words.every(word => name.includes(word) || vendors.includes(word));
        element.hidden = !matches;
        if (matches) element.style.removeProperty('display');
        else element.style.setProperty('display', 'none');
        if (matches) count += 1;
        if (item.count) item.count.textContent = item.originalCount;
        if (item.link && item.originalHref) item.link.setAttribute('href', item.originalHref);
        if (!matches || !brandSearch || !index) return;
        const isParent = item.isParent;
        const setCount = number => {
          if (currentRevision !== revision || !item.count) return;
          item.count.textContent = isParent
            ? (isArabic ? `${number} أقسام فرعية مطابقة` : `${number} matching subcategories`)
            : (isArabic ? `${number} منتج مطابق` : `${number} matching products`);
        };
        if (isParent) {
          setCount(index.children.filter(child => vendorsIn(child).some(vendor => names.includes(vendor.name))).length);
          if (item.link) {
            const url = new URL(item.originalHref, window.location.origin);
            url.searchParams.set('category_q', value);
            item.link.setAttribute('href', url.pathname + url.search);
          }
        } else {
          if (item.link) {
            const url = filteredURL(item.originalHref, names);
            url.searchParams.set('category_q', value);
            item.link.setAttribute('href', url.pathname + url.search);
          }
          // A leaf collection's facet counts are exact across all product pages.
          const directVendors = (index.vendors || []).filter(vendor => names.includes(vendor.name));
          if (directVendors.length === names.length && directVendors.every(vendor => Number.isInteger(vendor.count))) {
            setCount(directVendors.reduce((total, vendor) => total + vendor.count, 0));
          } else if (item.originalHref) {
            if (item.count) item.count.textContent = isArabic ? 'جارٍ حساب المنتجات المطابقة…' : 'Counting matching products…';
            const job = loadCount(item.originalHref, names, currentRevision).then(number => {
              if (currentRevision !== revision) return;
              if (number === 0) {
                const clearEmptyVendors = node => {
                  if (node?.url === index.url) (node.vendors || []).forEach(vendor => {
                    if (names.includes(vendor.name)) vendor.count = 0;
                  });
                  (node?.children || []).forEach(clearEmptyVendors);
                };
                items.forEach(other => clearEmptyVendors(other.index));
                foundEmpty = true;
              } else setCount(number);
            }).catch(() => {
              if (currentRevision === revision && item.count) item.count.textContent = isArabic ? 'تعذّر تأكيد العدد' : 'Count unavailable';
            });
            pendingCounts.push(job);
          }
        }
      });
      if (pendingCounts.length) Promise.allSettled(pendingCounts).then(() => {
        if (foundEmpty && currentRevision === revision) filter(value);
      });
      summaryText.textContent = isArabic ? `نتائج البحث عن «${value.trim()}»: ${count} أقسام مطابقة` : `Results for “${value.trim()}”: ${count} matching categories`;
      summary.hidden = !query;
      message.textContent = isArabic ? 'لا توجد أقسام مطابقة لبحثك' : 'No matching categories';
      message.hidden = count > 0 || !query;
      inputs.forEach((input) => {
        if (input.value !== value) input.value = value;
        const component = input.closest('.header-search')?.rwadyCollectionSearchComponent;
        if (component) component.query = value;
        const clear = input.form?.querySelector('[data-rwady-search-clear]');
        if (clear) {
          clear.hidden = !value.trim();
          clear.setAttribute('aria-hidden', String(clear.hidden));
        }
      });
    };

    inputs.forEach((input) => {
      const form = input.form;
      if (!form) return;
      input.placeholder = isArabic ? 'ابحث باسم القسم أو الماركة' : 'Search by category or brand';
      input.setAttribute('aria-label', input.placeholder);
      input.setAttribute('autocomplete', 'off');
      form.addEventListener('input', (event) => {
        if (event.target !== input) return;
        event.stopImmediatePropagation();
        filter(input.value);
      }, true);
      form.addEventListener('keydown', (event) => {
        if (event.target !== input) return;
        event.stopImmediatePropagation();
        if (event.key === 'Enter') {
          event.preventDefault();
          filter(input.value);
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          filter('');
        }
      }, true);
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        filter(input.value);
        input.closest('.header-search')?.rwadyCollectionSearchComponent?.close();
      }, true);
      form.querySelector('[data-rwady-search-clear]')?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        filter('');
        input.focus();
      });
    });
    const restoreQuery = () => filter(new URLSearchParams(window.location.search).get('category_q') || '');
    filter(new URLSearchParams(window.location.search).get('category_q') ?? inputs.find((input) => input.value)?.value ?? '');
    window.addEventListener('pageshow', () => requestAnimationFrame(restoreQuery));
    window.addEventListener('popstate', restoreQuery);
  };

  const initializeUnifiedCollectionSearch = () => {
    const stateApi = window.RwadyCollectionSearchStateApi;
    const scopeElement = document.querySelector('[data-rwady-collection-search-scope]');
    const mobileSearch = scopeElement?.closest('.header-search');
    const form = mobileSearch?.querySelector('form[role="search"]');
    let input = form?.querySelector('[data-rwady-search-input]');
    const destination = document.getElementById('ProductGridContainer');
    if (!stateApi || !scopeElement || !form || !input || !destination) return;

    const discountSearch = scopeElement.dataset.rwadyDiscountSearch === 'true';
    const initialParams = new URLSearchParams(window.location.search);
    const categoryContext = initialParams.has('category_q') && initialParams.has('filter.p.vendor');
    const searchRoots = categoryContext ? Array.from(document.querySelectorAll('.header-search')) : discountSearch
      ? Array.from(document.querySelectorAll('[data-rwady-discount-search="true"]')).map(scope => scope.closest('.header-search'))
      : [mobileSearch];
    const searchInputs = searchRoots.map(root => root.querySelector('[data-rwady-search-input]')).filter(Boolean);
    const ownsForm = candidate => searchInputs.some(field => field.form === candidate);
    const normalizer = window.RwadySearchNormalization;
    const normalizeQuery = (query) => normalizer?.createRwadySearchParams(query)?.q || query;
    const initialScope = !discountSearch && !categoryContext && scopeElement.dataset.rwadySearchWithinCollection === 'false' ? 'all' : 'collection';
    const hasInitialSearchState = initialParams.has('rwady_scope') || initialParams.has('rwady_q');
    const loadingIndicator = form.querySelector('[data-rwady-search-loading-indicator]');
    const clearButton = form.querySelector('[data-rwady-collection-search-clear]');

    if (hasInitialSearchState) {
      form.dataset.rwadySearchLoading = 'true';
      loadingIndicator?.setAttribute('aria-hidden', 'false');
      destination.dataset.rwadyRestorePending = 'true';
      destination.setAttribute('aria-busy', 'true');
      destination.hidden = true;
    }

    const controller = {
      state: stateApi.fromParams(initialParams, { scope: initialScope, q: categoryContext ? '' : input.value }),
      categoryQuery: categoryContext ? initialParams.get('category_q') : '',
      categoryVendors: initialParams.getAll('filter.p.vendor'),
      sectionId: '',
      collectionPath: discountSearch ? window.location.pathname : (scopeElement.dataset.rwadyCollectionPath || window.location.pathname),
      pageView: initialParams.get('view') || destination.querySelector('.rwady-page-controls')?.dataset.pageView || '',
      facets: null,
      cache: new Map(),
      responseCache: new Map(),
      discountValues: new Map(),
      abortController: null,
      requestToken: 0,
      suppressInput: false,
      lastInputValue: input.value,
      hydrated: false,
      clearPromise: null,
      activeStateKey: '',
      initialRestorePending: hasInitialSearchState,
      initialRestoreScheduled: false,

      displayQuery() {
        return this.state.q || this.categoryQuery;
      },

      paintCategoryContext() {
        if (this.categoryQuery) {
          const vendors = this.state.filters.filter(([key]) => key === 'filter.p.vendor').map(([, value]) => value);
          if (vendors.length !== this.categoryVendors.length || vendors.some(value => !this.categoryVendors.includes(value))) this.categoryQuery = '';
        }
        if (!this.categoryQuery && !this.categoryNotice) return;
        if (!this.categoryNotice) {
          this.categoryNotice = document.createElement('div');
          this.categoryNotice.className = 'rwady-category-search-context';
          this.categoryNotice.setAttribute('role', 'status');
          this.categoryNotice.setAttribute('aria-live', 'polite');
          this.categoryNoticeText = document.createElement('span');
          const clear = document.createElement('button');
          clear.type = 'button';
          clear.textContent = document.documentElement.lang.startsWith('ar') ? 'مسح البحث وعرض كل منتجات القسم' : 'Clear search and show all products in this category';
          clear.addEventListener('click', () => this.clearQuery());
          this.categoryNotice.append(this.categoryNoticeText, clear);
          destination.before(this.categoryNotice);
        }
        this.categoryNotice.hidden = !this.categoryQuery;
        this.categoryNoticeText.textContent = document.documentElement.lang.startsWith('ar')
          ? `البحث مفعّل: «${this.categoryQuery}» — تظهر المنتجات المطابقة فقط داخل هذا القسم`
          : `Search active: “${this.categoryQuery}” — showing only matching products in this category`;
      },

      claims() {
        return true;
      },

      attachFacets(facets, sectionId) {
        this.facets = facets;
        this.sectionId = sectionId;
        this.captureDiscountValue(document, 'collection');
        if (!this.hydrated) {
          this.hydrated = true;
          if ((hasInitialSearchState || this.state.scope === 'all' || this.state.q) && !this.initialRestoreScheduled) {
            this.initialRestoreScheduled = true;
            queueMicrotask(() => this.execute({ historyMode: 'none' }));
          }
        }
        return true;
      },

      setLoading(loading) {
        if (discountSearch) {
          if (loading) this.showSearchFeedback('loading');
          else if (this.feedback?.dataset.state === 'loading') this.feedback.hidden = true;
        }
        searchRoots.forEach(root => {
          root.querySelector('form').dataset.rwadySearchLoading = String(loading);
          root.querySelector('[data-rwady-search-loading-indicator]')?.setAttribute('aria-hidden', String(!loading));
        });
        destination.toggleAttribute('aria-busy', loading);
        if (this.facets) this.facets.isLoading = loading;
      },

      showSearchFeedback(state) {
        if (!destination.parentElement) return;
        if (!this.feedback) {
          this.feedback = document.createElement('div');
          this.feedback.className = 'rwady-collection-search-feedback';
          this.feedback.setAttribute('role', 'status');
          this.feedback.setAttribute('aria-live', 'polite');
          destination.before(this.feedback);
        }
        const arabic = document.documentElement.lang.startsWith('ar');
        this.feedback.dataset.state = state;
        this.feedback.hidden = false;
        this.feedback.textContent = state === 'loading'
          ? (arabic ? 'جارٍ البحث داخل التخفيضات…' : 'Searching within discounts…')
          : (arabic ? 'تعذّر تحميل نتائج البحث. حاول مرة أخرى.' : 'Search results could not be loaded. Please try again.');
        if (state === 'error') {
          const retry = document.createElement('button');
          retry.type = 'button';
          retry.textContent = arabic ? 'إعادة المحاولة' : 'Retry';
          retry.addEventListener('click', () => this.execute({ historyMode: 'replace' }));
          this.feedback.append(retry);
        }
      },

      finishInitialRestore() {
        if (!this.initialRestorePending) return;
        this.initialRestorePending = false;
        delete destination.dataset.rwadyRestorePending;
        destination.hidden = false;
      },

      paint() {
        this.paintCategoryContext();
        const withinCollection = this.state.scope === 'collection';
        scopeElement.dataset.rwadySearchWithinCollection = String(withinCollection);
        scopeElement.dataset.rwadySearchQuery = this.state.q;
        scopeElement.querySelectorAll('[data-rwady-search-scope-value]').forEach((button) => {
          const active = button.dataset.rwadySearchScopeValue === this.state.scope;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
          button.style.setProperty('background-color', active ? '#0b4bc4' : '#fff', 'important');
          button.style.setProperty('color', active ? '#fff' : '#0941ad', 'important');
          button.style.setProperty('box-shadow', active ? '0 4px 10px rgba(9, 65, 173, .18)' : 'none', 'important');
        });
        this.syncClearButton(this.displayQuery());
      },

      syncClearButton(value) {
        const visible = Boolean(String(value ?? '').trim());
        searchRoots.forEach(root => {
          const button = root.querySelector('[data-rwady-search-clear]');
          if (!button) return;
          button.hidden = !visible;
          button.setAttribute('aria-hidden', String(!visible));
        });
      },

      syncInput() {
        const value = this.displayQuery();
        if (input.value !== value) {
          this.suppressInput = true;
          input.value = value;
          input.dispatchEvent(new Event('input', { bubbles: true }));
          this.suppressInput = false;
        }
        searchInputs.forEach(field => {
          field.value = value;
          const component = field.closest('.header-search')?.rwadyCollectionSearchComponent;
          if (component) {
            component.query = value;
            if (categoryContext) component.collectionSearchEnabled = true;
          }
        });
        this.lastInputValue = value;
        this.syncClearButton(value);
      },

      updateHistory(mode) {
        if (mode === 'none') return;
        const params = stateApi.publicParams(this.state, normalizeQuery(this.state.q));
        if (this.categoryQuery) params.set('category_q', this.categoryQuery);
        if (discountSearch && this.pageView) params.set('view', this.pageView);
        const url = `${this.collectionPath}${params.toString() ? `?${params}` : ''}`;
        const method = mode === 'replace' ? 'replaceState' : 'pushState';
        history[method]({ rwadyCollectionSearch: true, searchParams: params.toString() }, '', url);
      },

      captureDiscountValue(sourceDocument, source) {
        const field = sourceDocument.querySelector(`input[name="${stateApi.DISCOUNT_PARAM}"]`);
        if (field?.value) this.discountValues.set(source, field.value);
      },

      async fetchHTML(url, signal) {
        if (discountSearch && this.pageView) {
          const scopedURL = new URL(url, window.location.origin);
          scopedURL.searchParams.set('view', this.pageView);
          url = scopedURL.pathname + scopedURL.search;
        }
        if (this.responseCache.has(url)) return this.responseCache.get(url);
        const response = await fetch(url, { signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`Collection search request failed with status ${response.status}`);
        const html = await response.text();
        this.responseCache.set(url, html);
        if (this.responseCache.size > 30) this.responseCache.delete(this.responseCache.keys().next().value);
        return html;
      },

      parse(html) {
        return new DOMParser().parseFromString(html, 'text/html');
      },

      sourceKey(state) {
        if (state.scope === 'collection') return 'collection';
        return state.q ? 'search' : 'all-products';
      },

      async resolveDiscountValue(state, signal) {
        if (!state.discount) return '';
        const source = this.sourceKey(state);
        if (this.discountValues.has(source)) return this.discountValues.get(source);

        const preflightState = stateApi.normalizeState({ ...state, discount: false, page: 1 });
        const preflight = stateApi.request(preflightState, {
          root: window.Shopify?.routes?.root || '/',
          collectionPath: this.collectionPath,
          sectionId: this.sectionId,
          normalizedQuery: normalizeQuery(preflightState.q),
        });
        const documentSource = this.parse(await this.fetchHTML(preflight.url, signal));
        this.captureDiscountValue(documentSource, source);
        return this.discountValues.has(source) ? this.discountValues.get(source) : null;
      },

      maxPage(documentSource) {
        let max = 1;
        documentSource.querySelectorAll('#ProductGridContainer *').forEach((element) => {
          Array.from(element.attributes).forEach((attribute) => {
            if (!attribute.value.includes('page=')) return;
            const matches = attribute.value.matchAll(/[?&]page=(\d+)/g);
            for (const match of matches) max = Math.max(max, Number(match[1]) || 1);
          });
        });
        return max;
      },

      appendEmptyState(grid, query, allProducts) {
        const emptyState = document.createElement('div');
        emptyState.id = 'RwadyCollectionSearchEmpty';
        emptyState.className = 'rwady-collection-search-empty';
        emptyState.setAttribute('role', 'status');
        const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        icon.setAttribute('viewBox', '0 0 64 64');
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML = '<circle cx="29" cy="27" r="17"></circle><path d="m42 40 13 13M10 43l12-12M10 31l12 12"></path>';
        const message = document.createElement('p');
        let template = scopeElement.dataset.rwadyEmptyMessage || 'No products matching “[query]”';
        if (allProducts) template = template.replace(' داخل هذا القسم', '').replace(' in this category', '');
        const parts = template.split('[query]');
        const queryText = document.createElement('bdi');
        queryText.textContent = query;
        message.append(document.createTextNode(parts[0] || ''), queryText, document.createTextNode(parts.slice(1).join('[query]')));
        emptyState.append(icon, message);
        grid.append(emptyState);
      },

      setCount(documentSource, count) {
        const label = (scopeElement.dataset.rwadyCountMessage || '[count] products').replace('[count]', String(count));
        const countSource = documentSource.getElementById('FacetsProductCount');
        if (countSource) countSource.textContent = label;
        const searchCount = documentSource.getElementById('ResultCount');
        if (searchCount) searchCount.textContent = String(count);
        return label;
      },

      buildEmptyResult(baseHTML, state) {
        const documentSource = this.parse(baseHTML);
        const grid = documentSource.querySelector('#ProductGridContainer [id^="ItemsGrid"]');
        if (grid) {
          grid.replaceChildren();
          this.appendEmptyState(grid, state.q, state.scope === 'all');
        }
        documentSource.querySelectorAll('#ProductGridContainer .pagination, #ProductGridContainer .pagination-search, #ProductGridContainer [id^="btn-pagination-"]').forEach((element) => {
          const wrapper = element.closest('.flex.justify-center') || element.parentElement;
          (wrapper || element).remove();
        });
        if (discountSearch) this.updateDiscountPagination(documentSource, state, 0, 25, 1);
        const countLabel = this.setCount(documentSource, 0);
        return { html: documentSource.documentElement.outerHTML, count: 0, countLabel };
      },

      async buildCollectionQueryResult(state, firstHTML, discountValue, signal) {
        const firstDocument = this.parse(firstHTML);
        let pageCount = this.maxPage(firstDocument);
        const documents = [firstDocument];
        const batchSize = discountSearch ? 4 : 1;
        for (let start = 2; start <= pageCount && start <= 100; start += batchSize) {
          const pageNumbers = Array.from({ length: Math.min(batchSize, pageCount - start + 1, 101 - start) }, (_, index) => start + index);
          const batch = await Promise.all(pageNumbers.map(async page => {
            const pageState = stateApi.normalizeState({ ...state, page });
            const request = stateApi.request(pageState, {
              root: window.Shopify?.routes?.root || '/',
              collectionPath: this.collectionPath,
              sectionId: this.sectionId,
              normalizedQuery: normalizeQuery(state.q),
              discountValue,
            });
            return this.parse(await this.fetchHTML(request.url, signal));
          }));
          documents.push(...batch);
          batch.forEach(pageDocument => { pageCount = Math.max(pageCount, this.maxPage(pageDocument)); });
        }

        if (discountSearch && pageCount > 100) throw new Error('Discount collection search exceeds page limit');
        const normalizedNeedle = normalizeQuery(state.q).trim().toLocaleLowerCase();
        const matches = [];
        documents.forEach((documentSource) => {
          documentSource.querySelectorAll('#ProductGridContainer #ItemsGrid > .grid-item').forEach((card) => {
            const text = card.textContent.replace(/\s+/g, ' ').trim().toLocaleLowerCase();
            if (text.includes(normalizedNeedle) && (!discountSearch || card.dataset.rwadyPriceDiscount === 'true')) matches.push(card.cloneNode(true));
          });
        });

        const grid = firstDocument.querySelector('#ProductGridContainer #ItemsGrid');
        if (!grid) throw new Error('Collection product grid was not found');
        if (discountSearch) {
          const controls = firstDocument.querySelector('.rwady-page-controls');
          const pageSize = Number(controls?.dataset.pageSize) || 25;
          const pages = Math.max(1, Math.ceil(matches.length / pageSize));
          const currentPage = Math.min(state.page, pages);
          grid.replaceChildren(...matches.slice((currentPage - 1) * pageSize, currentPage * pageSize));
          this.updateDiscountPagination(firstDocument, state, matches.length, pageSize, currentPage);
        } else {
          grid.replaceChildren(...matches);
        }
        firstDocument.querySelectorAll('#ProductGridContainer .pagination, #ProductGridContainer [id^="btn-pagination-"]').forEach((element) => {
          const wrapper = element.closest('.flex.justify-center') || element.parentElement;
          (wrapper || element).remove();
        });
        if (matches.length === 0) this.appendEmptyState(grid, state.q, false);
        const countLabel = this.setCount(firstDocument, matches.length);
        return { html: firstDocument.documentElement.outerHTML, count: matches.length, countLabel };
      },

      updateDiscountPagination(source, state, total, pageSize, currentPage) {
        const controls = source.querySelector('.rwady-page-controls');
        if (!controls) return;
        controls.querySelectorAll('.rwady-page-buttons, .rwady-page-position').forEach(node => node.remove());
        const arabic = document.documentElement.lang.startsWith('ar');
        const count = controls.querySelector('.rwady-page-count');
        const start = total ? (currentPage - 1) * pageSize + 1 : 0;
        const end = Math.min(currentPage * pageSize, total);
        if (count) {
          count.dataset.total = String(total);
          count.dataset.offset = String(start ? start - 1 : 0);
          count.textContent = arabic ? `عرض ${start}–${end} من ${total} منتج` : `Showing ${start}–${end} of ${total} products`;
        }
        const pages = Math.ceil(total / pageSize);
        if (pages <= 1) return;
        const nav = source.createElement('nav');
        nav.className = 'rwady-page-buttons';
        nav.setAttribute('aria-label', arabic ? 'ترقيم الصفحات' : 'Pagination');
        const pageLink = (page, label, className) => {
          const link = source.createElement('a');
          const params = stateApi.publicParams({ ...state, scope: 'collection', page }, normalizeQuery(state.q));
          if (this.pageView) params.set('view', this.pageView);
          link.href = `${this.collectionPath}?${params}`;
          link.textContent = label;
          link.className = className;
          return link;
        };
        const previous = currentPage > 1 ? pageLink(currentPage - 1, arabic ? 'السابق' : 'Previous', 'rwady-page-previous') : source.createElement('span');
        previous.className = 'rwady-page-previous';
        if (currentPage === 1) { previous.textContent = arabic ? 'السابق' : 'Previous'; previous.setAttribute('aria-disabled', 'true'); }
        const numbers = source.createElement('div');
        numbers.className = 'rwady-page-numbers';
        for (let page = 1; page <= pages; page += 1) {
          if (page !== 1 && page !== pages && Math.abs(page - currentPage) > 2) continue;
          const item = page === currentPage ? source.createElement('span') : pageLink(page, String(page), '');
          item.textContent = String(page);
          if (page === currentPage) item.setAttribute('aria-current', 'page');
          numbers.append(item);
        }
        const next = currentPage < pages ? pageLink(currentPage + 1, arabic ? 'التالي' : 'Next', 'rwady-page-next') : source.createElement('span');
        next.className = 'rwady-page-next';
        if (currentPage === pages) { next.textContent = arabic ? 'التالي' : 'Next'; next.setAttribute('aria-disabled', 'true'); }
        nav.append(previous, numbers, next);
        controls.append(nav);
      },

      async load(state, signal) {
        const normalizedQuery = normalizeQuery(state.q);
        const discountValue = await this.resolveDiscountValue(state, signal);
        if (state.discount && discountValue === null) {
          const baseState = stateApi.normalizeState({ ...state, discount: false, page: 1 });
          const baseRequest = stateApi.request(baseState, {
            root: window.Shopify?.routes?.root || '/',
            collectionPath: this.collectionPath,
            sectionId: this.sectionId,
            normalizedQuery,
          });
          return this.buildEmptyResult(await this.fetchHTML(baseRequest.url, signal), state);
        }
        const request = stateApi.request(discountSearch && state.q ? { ...state, page: 1 } : state, {
          root: window.Shopify?.routes?.root || '/',
          collectionPath: this.collectionPath,
          sectionId: this.sectionId,
          normalizedQuery,
          discountValue,
        });
        const firstHTML = await this.fetchHTML(request.url, signal);
        this.captureDiscountValue(this.parse(firstHTML), request.source);
        if (state.scope === 'collection' && state.q) {
          return this.buildCollectionQueryResult(state, firstHTML, discountValue, signal);
        }
        return { html: firstHTML, source: request.source };
      },

      render(result) {
        if (this.facets?.renderRwadyCollectionSearchResults) {
          this.facets.renderRwadyCollectionSearchResults(result.html, this.state.scope === 'all' && Boolean(this.state.q));
        } else {
          const parsed = this.parse(result.html);
          const source = parsed.getElementById('ProductGridContainer');
          if (source) destination.innerHTML = source.innerHTML;
        }
        const countLabel = result.countLabel || (categoryContext
          ? this.parse(result.html).querySelector('.rwady-filter-results-count')?.textContent.trim() : '');
        if (countLabel) {
          document.querySelectorAll('#ProductCount_header, #ProductCountDrawer, #ProductCountMobile, .rwady-filter-results-count').forEach((element) => {
            element.textContent = countLabel;
          });
        }
        destination.hidden = false;
        if (this.feedback) this.feedback.hidden = true;
        document.dispatchEvent(new CustomEvent('maximize:animation:reveal'));
      },

      async execute({ historyMode = 'push' } = {}) {
        if (!this.sectionId) return;
        this.state = stateApi.normalizeState(discountSearch ? { ...this.state, scope: 'collection' } : this.state);
        this.paint();
        this.syncInput();
        const normalizedQuery = normalizeQuery(this.state.q);
        const key = stateApi.cacheKey(this.state, normalizedQuery) + (discountSearch ? this.pageView : '');
        this.abortController?.abort();
        this.abortController = new AbortController();
        const token = ++this.requestToken;
        this.setLoading(true);

        try {
          let result = this.cache.get(key);
          if (!result) {
            result = await this.load(this.state, this.abortController.signal);
            if (token !== this.requestToken) return false;
            this.cache.set(key, result);
            if (this.cache.size > 20) this.cache.delete(this.cache.keys().next().value);
          }
          if (token !== this.requestToken) return;
          this.render(result);
          this.activeStateKey = key;
          this.syncInput();
          this.updateHistory(historyMode);
          return true;
        } catch (error) {
          if (error.name !== 'AbortError') console.error('Failed to load collection search state:', error);
          if (discountSearch && token === this.requestToken && error.name !== 'AbortError') this.showSearchFeedback('error');
          return false;
        } finally {
          this.facets?.releaseFilterDrawerLock?.();
          if (token === this.requestToken) {
            this.abortController = null;
            this.setLoading(false);
            this.finishInitialRestore();
            if (this.feedback?.dataset.state === 'error' && !this.feedback.hidden) destination.hidden = true;
          }
        }
      },

      submit(sourceInput = input) {
        input = sourceInput;
        input.closest('.header-search')?.rwadyCollectionSearchComponent?.close();
        const query = input.value === this.categoryQuery ? '' : input.value;
        this.state = stateApi.normalizeState({ ...this.state, q: query, page: 1 });
        return this.execute({ historyMode: 'push' });
      },

      setScope(scope) {
        if (discountSearch) scope = 'collection';
        if (scope === this.state.scope) return;
        this.state = stateApi.normalizeState({ ...this.state, scope, q: input.value === this.categoryQuery ? '' : input.value, page: 1 });
        this.execute({ historyMode: 'push' });
      },

      clearQuery() {
        if (!this.state.q && !input.value && !this.categoryQuery) return this.clearPromise || Promise.resolve();
        const filters = this.categoryQuery ? this.state.filters.filter(([key]) => key !== 'filter.p.vendor') : this.state.filters;
        this.categoryQuery = '';
        this.state = stateApi.normalizeState({ ...this.state, filters, q: '', page: 1 });
        this.paint();
        this.syncInput();
        this.updateHistory('push');
        const execution = this.execute({ historyMode: 'none' });
        this.clearPromise = Promise.resolve(execution).finally(() => {
          this.clearPromise = null;
        });
        return this.clearPromise;
      },

      handleInput(value) {
        if (this.suppressInput) return;
        const nextValue = String(value ?? '');
        const shouldClear = nextValue.trim() === ''
          && (this.lastInputValue.trim() !== '' || this.state.q !== '');
        this.lastInputValue = nextValue;
        scopeElement.dataset.rwadySearchQuery = nextValue;
        this.syncClearButton(nextValue);
        if (shouldClear) return this.clearQuery();
      },

      restoreFromHistory() {
        const params = new URLSearchParams(window.location.search);
        this.state = stateApi.fromParams(params, { scope: initialScope });
        this.categoryQuery = params.has('filter.p.vendor') ? (params.get('category_q') || '') : '';
        this.categoryVendors = params.getAll('filter.p.vendor');
        if (discountSearch) {
          this.state.scope = 'collection';
          this.pageView = new URLSearchParams(window.location.search).get('view') || this.pageView;
        }
        this.paint();
        this.syncInput();
        const key = stateApi.cacheKey(this.state, normalizeQuery(this.state.q)) + (discountSearch ? this.pageView : '');
        if (this.activeStateKey === key) return Promise.resolve();
        return Promise.resolve(this.execute({ historyMode: 'none' })).finally(() => {
          this.paint();
          this.syncInput();
        });
      },

      handleFacetSubmit(formElement) {
        const params = new URLSearchParams(new FormData(formElement));
        const desktopSort = formElement.querySelector('.rwady-desktop-sort-filter__select');
        if (desktopSort && window.innerWidth >= 1024) params.set('sort_by', desktopSort.value);
        this.state = stateApi.fromForm(params, this.state);
        this.execute({ historyMode: 'push' });
        return true;
      },

      handleFilterURL(url) {
        const parsedURL = new URL(url, window.location.origin);
        this.state = stateApi.fromForm(parsedURL.searchParams, this.state);
        this.execute({ historyMode: 'push' });
        return true;
      },

      handlePageSize(size, viewPrefix) {
        if (!discountSearch || !['25', '50', '75', '100'].includes(String(size))) return null;
        this.pageView = viewPrefix + size;
        this.state = stateApi.normalizeState({ ...this.state, page: 1, scope: 'collection' });
        return this.execute({ historyMode: 'push' });
      },

      handlePagination(url) {
        const page = Number(new URL(url, window.location.origin).searchParams.get('page')) || 1;
        this.state = stateApi.normalizeState({ ...this.state, page });
        this.execute({ historyMode: 'push' });
      },
    };

    if (discountSearch) controller.state.scope = 'collection';
    window.RwadyCollectionSearchController = controller;
    controller.paint();
    if (categoryContext) controller.syncInput();
    document.dispatchEvent(new CustomEvent('rwady:collection-search-controller-ready'));

    document.addEventListener('click', (event) => {
      const searchJumpButton = event.target.closest('[data-rwady-collection-search-jump]');
      if (searchJumpButton) {
        event.preventDefault();
        input.focus({ preventScroll: true });
        input.scrollIntoView({
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          block: 'center',
        });
        return;
      }

      const scopeButton = event.target.closest('[data-rwady-search-scope-value]');
      if (scopeButton && scopeElement.contains(scopeButton)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        controller.setScope(scopeButton.dataset.rwadySearchScopeValue);
        return;
      }

      const clearButton = event.target.closest(categoryContext ? '[data-rwady-search-clear]' : '[data-rwady-collection-search-clear]');
      if (clearButton && searchRoots.some(root => root.contains(clearButton))) {
        input = clearButton.closest('form').querySelector('[data-rwady-search-input]');
        event.preventDefault();
        event.stopImmediatePropagation();
        controller.clearQuery();
        input.focus();
        return;
      }

      const paginationLink = event.target.closest('#ProductGridContainer .pagination a[href], #ProductGridContainer .pagination-search a[href], #ProductGridContainer .rwady-page-controls a[href]');
      if (paginationLink) {
        event.preventDefault();
        event.stopImmediatePropagation();
        controller.handlePagination(paginationLink.href);
      }
    }, true);

    document.addEventListener('submit', (event) => {
      if (!ownsForm(event.target)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      controller.submit(event.target.querySelector('[data-rwady-search-input]'));
    }, true);

    document.addEventListener('input', (event) => {
      if (!searchInputs.includes(event.target)) return;
      input = event.target;
      controller.handleInput(input.value);
    }, true);

    document.addEventListener('change', event => {
      const select = event.target.closest('.rwady-page-size-select');
      if (!discountSearch || !select || !destination.contains(select)) return;
      const controls = select.closest('.rwady-page-controls');
      event.stopImmediatePropagation();
      controller.handlePageSize(select.value, controls.dataset.viewPrefix);
    }, true);

    window.addEventListener('popstate', () => {
      controller.restoreFromHistory();
    });
    if (categoryContext) window.addEventListener('pageshow', () => requestAnimationFrame(() => controller.syncInput()));
  };

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const playAnimation = (element, keyframes, options) => {
    if (!element || prefersReducedMotion() || typeof element.animate !== 'function') return null;
    activeAnimations.get(element)?.cancel();
    const animation = element.animate(keyframes, options);
    activeAnimations.set(element, animation);
    const cleanup = () => {
      if (activeAnimations.get(element) === animation) activeAnimations.delete(element);
    };
    animation.addEventListener('finish', cleanup, { once: true });
    animation.addEventListener('cancel', cleanup, { once: true });
    return animation;
  };

  const pulseElements = (selector, scale) => {
    document.querySelectorAll(selector).forEach((element) => {
      playAnimation(element, [
        { transform: 'scale(1)' },
        { transform: `scale(${scale})`, offset: 0.82 },
        { transform: 'scale(1)' },
      ], {
        duration: 220,
        easing: 'cubic-bezier(.34, 1.56, .64, 1)',
      });
    });
  };

  const celebrateWishlist = () => {
    document.querySelectorAll('[data-rwady-wishlist-link]').forEach((element) => {
      const heart = element.querySelector('svg') || element;
      playAnimation(heart, [
        { transform: 'scale(1) rotate(0deg)', filter: 'drop-shadow(0 0 0 rgba(220, 38, 38, 0))' },
        { transform: 'scale(1.48) rotate(-7deg)', filter: 'drop-shadow(0 0 10px rgba(220, 38, 38, .8))', offset: .16 },
        { transform: 'scale(.92) rotate(3deg)', filter: 'drop-shadow(0 0 3px rgba(220, 38, 38, .35))', offset: .3 },
        { transform: 'scale(1) rotate(0deg)', filter: 'drop-shadow(0 0 0 rgba(220, 38, 38, 0))', offset: .5 },
        { transform: 'scale(1.42) rotate(6deg)', filter: 'drop-shadow(0 0 10px rgba(220, 38, 38, .8))', offset: .7 },
        { transform: 'scale(.94) rotate(-2deg)', filter: 'drop-shadow(0 0 3px rgba(220, 38, 38, .3))', offset: .84 },
        { transform: 'scale(1) rotate(0deg)', filter: 'drop-shadow(0 0 0 rgba(220, 38, 38, 0))' },
      ], {
        duration: 1250,
        easing: 'cubic-bezier(.34, 1.56, .64, 1)',
      });
    });
  };

  const readWishlist = () => {
    try {
      const stored = JSON.parse(window.localStorage.getItem(WISHLIST_STORAGE_KEY) || '[]');
      if (!Array.isArray(stored)) return [];
      return stored
        .map((item) => {
          if (item && typeof item === 'object') {
            return { id: String(item.id || ''), handle: String(item.handle || '') };
          }
          return { id: String(item || ''), handle: '' };
        })
        .filter((item) => item.id);
    } catch (_error) {
      return [];
    }
  };

  const writeWishlist = (productIds) => {
    try {
      window.localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(productIds));
    } catch (_error) {
      return false;
    }
    return true;
  };

  // Preserve display data separately from membership; an unpublished product is
  // still a saved product. Never restore cached purchase controls.
  const wishlistSnapshotKey = 'rwady-wishlist-snapshots-v1';
  const readWishlistSnapshots = () => {
    try { return JSON.parse(localStorage.getItem(wishlistSnapshotKey) || '{}') || {}; }
    catch (_) { return {}; }
  };
  const rememberWishlistProduct = (product) => {
    if (!product?.id) return;
    const snapshots = readWishlistSnapshots();
    snapshots[String(product.id)] = {
      title: String(product.title || '').slice(0, 500),
      image: String(product.featured_image || '').slice(0, 2000)
    };
    const saved = new Set(readWishlist().map(item => item.id));
    Object.keys(snapshots).forEach(id => { if (!saved.has(id)) delete snapshots[id]; });
    try { localStorage.setItem(wishlistSnapshotKey, JSON.stringify(snapshots)); } catch (_) {}
  };
  const loadWishlistProduct = async (rootUrl, handle) => {
    if (!handle) return { missing: true };
    const url = new URL(`products/${encodeURIComponent(handle)}.js`, `${location.origin}${rootUrl}`);
    const response = await fetch(url, { cache: 'no-store' });
    if (response.status === 404 || response.status === 410) return { missing: true };
    if (!response.ok) throw new Error('Product availability could not be checked');
    return { product: await response.json() };
  };
  const unavailableWishlistCard = (item, unknown = false) => {
    const ar = document.documentElement.lang.startsWith('ar');
    const snapshot = readWishlistSnapshots()[item.id] || {};
    const card = document.createElement('article');
    card.className = 'grid-item rwady-collection-product-grid__item';
    card.setAttribute('data-rwady-wishlist-card', '');
    card.style.cssText = 'padding:16px;border:1px solid #d5dfef;border-radius:14px;background:white;display:flex;flex-direction:column;gap:12px';
    if (/^(https:\/\/|\/\/)/.test(snapshot.image || '')) {
      const img = document.createElement('img');
      img.src = snapshot.image;
      img.alt = snapshot.title || '';
      img.loading = 'lazy';
      img.style.cssText = 'width:100%;aspect-ratio:1;object-fit:contain';
      card.append(img);
    }
    const title = document.createElement('strong');
    title.textContent = snapshot.title || item.handle.replace(/-/g, ' ') || (ar ? 'منتج محفوظ' : 'Saved product');
    card.append(title);
    const badge = document.createElement('span');
    badge.textContent = unknown ? (ar ? 'تعذر التحقق من التوفر، حاول مجددًا' : 'Unable to check availability. Please retry.') : (ar ? 'غير متوفر — Out of stock' : 'Out of stock');
    badge.style.color = '#6b7280';
    card.append(badge);
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.setAttribute('data-rwady-wishlist-toggle', '');
    remove.dataset.productId = item.id;
    remove.dataset.productHandle = item.handle;
    remove.dataset.labelRemove = ar ? 'إزالة من المفضلة' : 'Remove from wishlist';
    remove.dataset.labelAdd = ar ? 'إضافة للمفضلة' : 'Add to wishlist';
    remove.textContent = remove.dataset.labelRemove;
    card.append(remove);
    return card;
  };

  const syncWishlistState = () => {
    const wishlist = readWishlist();
    const wishlistIds = new Set(wishlist.map((item) => item.id));
    const hasItems = wishlist.length > 0;

    document.querySelectorAll('[data-rwady-wishlist-link]').forEach((link) => {
      link.classList.toggle('has-items', hasItems);
      link.dataset.wishlistCount = String(wishlist.length);
    });

    document.querySelectorAll('[data-rwady-wishlist-toggle]').forEach((button) => {
      const isActive = wishlistIds.has(String(button.dataset.productId));
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
      button.setAttribute('aria-label', isActive ? button.dataset.labelRemove : button.dataset.labelAdd);
    });
  };

  const toggleWishlistProduct = (button, removeFromCart = false) => {
    const productId = String(button.dataset.productId || '');
    if (!productId) return;

    const wishlist = readWishlist();
    const existingIndex = wishlist.findIndex((item) => item.id === productId);
    const isMoveAction = button.hasAttribute('data-rwady-wishlist-move');
    if (existingIndex === -1) {
      wishlist.push({ id: productId, handle: String(button.dataset.productHandle || '') });
    } else if (!isMoveAction) {
      wishlist.splice(existingIndex, 1);
    }

    if (writeWishlist(wishlist)) {
      if (existingIndex === -1) loadWishlistProduct(window.Shopify?.routes?.root || '/', button.dataset.productHandle)
        .then(result => { if (result.product) rememberWishlistProduct(result.product); }).catch(() => {});
      syncWishlistState();
      if (isMoveAction || existingIndex === -1) {
        celebrateWishlist();
      } else {
        pulseElements('[data-rwady-wishlist-link]', 1.22);
      }
      document.dispatchEvent(new CustomEvent('rwady:wishlist-change', {
        detail: { productId, isActive: isMoveAction || existingIndex === -1, count: wishlist.length },
      }));

      if (isMoveAction && removeFromCart) {
        const removeButton = button.closest('.cart-item-card')?.querySelector('.js-remove-item');
        if (removeButton) {
          removeButton.dataset.rwadyRemoveConfirmed = 'true';
          removeButton.click();
        }
      }
    }
  };

  const openWishlistChoiceDialog = (button) => {
    const isArabic = document.documentElement.dir === 'rtl'
      || document.documentElement.lang?.toLowerCase().startsWith('ar');
    const dialog = document.createElement('dialog');
    dialog.className = 'rwady-wishlist-choice-dialog';
    dialog.dir = isArabic ? 'rtl' : 'ltr';
    dialog.setAttribute('aria-labelledby', 'RwadyWishlistChoiceTitle');
    dialog.innerHTML = `
      <div class="rwady-wishlist-choice-dialog__panel">
        <button class="rwady-wishlist-choice-dialog__close" type="button" data-wishlist-choice="cancel" aria-label="${isArabic ? 'إغلاق' : 'Close'}">×</button>
        <span class="rwady-wishlist-choice-dialog__heart" aria-hidden="true">♥</span>
        <h2 id="RwadyWishlistChoiceTitle">${isArabic ? 'إضافة إلى المفضلة' : 'Add to wishlist'}</h2>
        <p>${isArabic ? 'هل تريد إبقاء المنتج في السلة أم حذفه منها؟' : 'Would you like to keep this product in your cart or remove it?'}</p>
        <button class="rwady-wishlist-choice-dialog__primary" type="button" data-wishlist-choice="add-only">${isArabic ? 'إضافة للمفضلة فقط' : 'Add to wishlist only'}</button>
        <button class="rwady-wishlist-choice-dialog__secondary" type="button" data-wishlist-choice="add-remove">${isArabic ? 'إضافة للمفضلة وحذف من السلة' : 'Add to wishlist and remove from cart'}</button>
        <button class="rwady-wishlist-choice-dialog__cancel" type="button" data-wishlist-choice="cancel">${isArabic ? 'إلغاء' : 'Cancel'}</button>
      </div>`;

    const closeDialog = () => {
      dialog.close();
      dialog.remove();
    };
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) {
        closeDialog();
        return;
      }
      const choice = event.target.closest('[data-wishlist-choice]')?.dataset.wishlistChoice;
      if (!choice) return;
      if (choice === 'add-only') toggleWishlistProduct(button, false);
      if (choice === 'add-remove') toggleWishlistProduct(button, true);
      closeDialog();
    });
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDialog();
    });
    document.body.append(dialog);
    dialog.showModal();
  };

  const addRemovedProductToWishlist = (button) => {
    const productId = String(button.dataset.productId || '');
    if (!productId) return;
    const wishlist = readWishlist();
    if (!wishlist.some((item) => item.id === productId)) {
      wishlist.push({ id: productId, handle: String(button.dataset.productHandle || '') });
    }
    if (!writeWishlist(wishlist)) return;
    loadWishlistProduct(window.Shopify?.routes?.root || '/', button.dataset.productHandle)
      .then(result => { if (result.product) rememberWishlistProduct(result.product); }).catch(() => {});
    syncWishlistState();
    celebrateWishlist();
    document.dispatchEvent(new CustomEvent('rwady:wishlist-change', {
      detail: { productId, isActive: true, count: wishlist.length },
    }));
  };

  const confirmCartRemoval = (button) => {
    const isArabic = document.documentElement.dir === 'rtl'
      || document.documentElement.lang?.toLowerCase().startsWith('ar');
    const dialog = document.createElement('dialog');
    dialog.className = 'rwady-wishlist-choice-dialog rwady-cart-remove-dialog';
    dialog.dir = isArabic ? 'rtl' : 'ltr';
    dialog.setAttribute('aria-labelledby', 'RwadyCartRemoveTitle');
    dialog.innerHTML = `
      <div class="rwady-wishlist-choice-dialog__panel">
        <button class="rwady-wishlist-choice-dialog__close" type="button" data-remove-choice="cancel" aria-label="${isArabic ? 'إغلاق' : 'Close'}">×</button>
        <span class="rwady-cart-remove-dialog__icon" aria-hidden="true">♥</span>
        <h2 id="RwadyCartRemoveTitle">${isArabic ? 'إزالة المنتج من السلة؟' : 'Remove product from cart?'}</h2>
        <p>${isArabic ? 'هل ترغب بإضافته إلى المفضلة لسهولة الوصول إليه لاحقًا؟' : 'Would you like to add it to your wishlist for easier access later?'}</p>
        <button class="rwady-wishlist-choice-dialog__primary" type="button" data-remove-choice="wishlist">${isArabic ? 'نعم، أضفه للمفضلة ثم احذفه' : 'Add to wishlist and remove'}</button>
        <button class="rwady-cart-remove-dialog__remove" type="button" data-remove-choice="remove">${isArabic ? 'لا، احذفه فقط' : 'No, remove only'}</button>
        <button class="rwady-wishlist-choice-dialog__cancel" type="button" data-remove-choice="cancel">${isArabic ? 'إلغاء' : 'Cancel'}</button>
      </div>`;

    const closeDialog = () => {
      dialog.close();
      dialog.remove();
    };
    const continueRemoval = (addToWishlist) => {
      if (addToWishlist) addRemovedProductToWishlist(button);
      button.dataset.rwadyRemoveConfirmed = 'true';
      button.click();
    };
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) {
        closeDialog();
        return;
      }
      const choice = event.target.closest('[data-remove-choice]')?.dataset.removeChoice;
      if (!choice) return;
      if (choice === 'wishlist') continueRemoval(true);
      if (choice === 'remove') continueRemoval(false);
      closeDialog();
    });
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDialog();
    });
    document.body.append(dialog);
    dialog.showModal();
  };

  const fetchWishlistProductCard = async (rootUrl, handle) => {
    const cardUrl = new URL(`products/${encodeURIComponent(handle)}`, `${window.location.origin}${rootUrl}`);
    cardUrl.searchParams.set('view', 'wishlist-card');
    const response = await fetch(cardUrl.toString(), { cache: 'no-store' });
    if (!response.ok) return null;

    const template = document.createElement('template');
    template.innerHTML = await response.text();
    const card = template.content.querySelector('[data-rwady-wishlist-card]');
    card?.querySelectorAll('.anm-reveal-item').forEach((item) => item.classList.remove('anm-reveal-item'));
    return card;
  };

  let wishlistRenderVersion = 0;
  const renderWishlistPage = async () => {
    const version = ++wishlistRenderVersion;
    const page = document.querySelector('[data-rwady-wishlist-page]');
    if (!page) return;
    const grid = page.querySelector('[data-rwady-wishlist-grid]');
    const empty = page.querySelector('[data-rwady-wishlist-empty]');
    const wishlist = readWishlist();
    grid.replaceChildren();

    if (!wishlist.length) {
      empty.textContent = page.dataset.emptyLabel;
      empty.hidden = false;
      return;
    }

    const cards = await Promise.all(wishlist.map(async (item) => {
      try {
        const result = await loadWishlistProduct(page.dataset.rootUrl, item.handle);
        if (result.missing) return unavailableWishlistCard(item);
        rememberWishlistProduct(result.product);
        if (!result.product.available) return unavailableWishlistCard(item);
        return await fetchWishlistProductCard(page.dataset.rootUrl, item.handle) || unavailableWishlistCard(item, true);
      } catch (_error) {
        return unavailableWishlistCard(item, true);
      }
    }));
    if (version !== wishlistRenderVersion) return;
    cards.filter(Boolean).forEach((card) => grid.append(card));
    empty.hidden = grid.childElementCount > 0;
    if (!grid.childElementCount) {
      empty.textContent = page.dataset.emptyLabel;
    }
    syncWishlistState();
  };

  const initializeWishlist = () => {
    syncWishlistState();

    document.addEventListener('click', (event) => {
      const removeButton = event.target.closest('[data-rwady-cart-remove-confirm]');
      if (!removeButton) return;
      if (removeButton.dataset.rwadyRemoveConfirmed === 'true') {
        delete removeButton.dataset.rwadyRemoveConfirmed;
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      confirmCartRemoval(removeButton);
    }, true);

    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-rwady-wishlist-toggle]');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      if (button.hasAttribute('data-rwady-wishlist-move')) {
        openWishlistChoiceDialog(button);
      } else {
        toggleWishlistProduct(button);
      }
    });

    document.addEventListener('shopify:section:load', syncWishlistState);
    document.addEventListener('rwady:wishlist-change', renderWishlistPage);

    const wishlistControlSelector = '[data-rwady-wishlist-link], [data-rwady-wishlist-toggle]';
    const containsWishlistControl = (node) => (
      node.nodeType === 1
      && (
        node.matches(wishlistControlSelector)
        || node.querySelector(wishlistControlSelector)
      )
    );

    let wishlistSyncFrame;
    new MutationObserver((mutations) => {
      const addedWishlistControls = mutations.some((mutation) => (
        Array.from(mutation.addedNodes).some(containsWishlistControl)
      ));
      if (!addedWishlistControls) return;

      window.cancelAnimationFrame(wishlistSyncFrame);
      wishlistSyncFrame = window.requestAnimationFrame(syncWishlistState);
    }).observe(document.body, { childList: true, subtree: true });
    window.addEventListener('storage', (event) => {
      if (event.key === WISHLIST_STORAGE_KEY) {
        syncWishlistState();
        renderWishlistPage();
      }
    });
    renderWishlistPage();
  };

  const getCartBadges = () => document.querySelectorAll(
    '[data-rwady-cart-count], #cart-icon-bubble .cart-bubble__container'
  );

  const syncCartState = (cart) => {
    const fallbackCount = Number.parseInt(cart?.item_count, 10);
    const count = interactionUtils?.calculateCartCount(cart) ?? (Number.isFinite(fallbackCount) ? fallbackCount : 0);
    const formattedCount = interactionUtils?.formatCartCount(count) ?? (count > 99 ? '99+' : String(count));

    getCartBadges().forEach((badge) => {
      badge.hidden = count === 0;
      badge.dataset.countItem = String(count);
      badge.textContent = count === 0 ? '' : formattedCount;
    });

    document.querySelectorAll('[data-rwady-cart-trigger]').forEach((trigger) => {
      const emptyLabel = trigger.dataset.labelEmpty || trigger.getAttribute('aria-label') || 'Cart';
      trigger.setAttribute('aria-label', `${emptyLabel} (${count})`);
    });

    if (window.xCartHelper) window.xCartHelper.currentItemCount = count;
    document.dispatchEvent(new CustomEvent('rwady:cart-state', { detail: { cart, count } }));
  };

  const requestCartState = () => {
    cartStateRefreshQueued = true;
    if (cartStateRequest) return cartStateRequest;
    cartStateRequest = Promise.resolve().then(async () => {
      try {
        do {
          cartStateRefreshQueued = false;
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 10000);
          try {
            const root = window.Shopify?.routes?.root || '/';
            const response = await fetch(`${root}cart.js`, {
              headers: { Accept: 'application/json' },
              signal: controller.signal,
            });
            if (response.ok) {
              const cart = await response.json();
              if (!cartStateRefreshQueued) syncCartState(cart);
            }
          } catch (error) {
            console.error('Unable to refresh RWADY cart state:', error);
          } finally {
            clearTimeout(timeout);
          }
        } while (cartStateRefreshQueued);
      } finally {
        cartStateRequest = null;
      }
    });
    return cartStateRequest;
  };

  const findProductImage = (source) => {
    const wrapper = source?.closest(
      '.product-card__wrapper, .product-template__wrapper, .quick-view__wrapper, .sticky-add-to-cart__container'
    );
    const image = wrapper?.querySelector(
      '.product-card__image-wrapper .slide-item:first-child img, .product-card__image-wrapper img, .product-template__media img, img'
    );
    return image?.currentSrc || image?.src || '';
  };

  const captureAddSource = (source) => {
    const rect = source?.getBoundingClientRect();
    return {
      source,
      imageSrc: findProductImage(source),
      startX: rect ? rect.left + (rect.width / 2) : null,
      startY: rect ? rect.top + (rect.height / 2) : null,
    };
  };

  const findVisibleCartTarget = () => Array.from(document.querySelectorAll('[data-rwady-cart-fly-target]'))
    .find((element) => {
      const rect = element.getBoundingClientRect();
      const style = window.getComputedStyle(element);
      return rect.width > 0 && rect.height > 0 && style.visibility !== 'hidden' && style.display !== 'none';
    });

  const animateProductToCart = (detail) => {
    const canAnimate = interactionUtils?.shouldAnimateCartTransfer({
      confirmed: true,
      buyNow: Boolean(detail?.redirectToCheckout),
      reducedMotion: prefersReducedMotion(),
    }) ?? (!detail?.redirectToCheckout && !prefersReducedMotion());
    if (!canAnimate) return;

    const target = findVisibleCartTarget();
    const targetRect = target?.getBoundingClientRect();
    const startX = Number(detail?.context?.startX);
    const startY = Number(detail?.context?.startY);
    if (!targetRect || !Number.isFinite(startX) || !Number.isFinite(startY)) return;

    const floating = document.createElement('span');
    floating.className = 'rwady-cart-transfer';
    floating.setAttribute('aria-hidden', 'true');
    if (detail.context.imageSrc) {
      const image = document.createElement('img');
      image.src = detail.context.imageSrc;
      image.alt = '';
      image.decoding = 'async';
      floating.append(image);
    } else {
      floating.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 4h2l2.4 11.2h9.7L20 7H6M9 20a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm9 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z"/></svg>';
    }
    floating.style.left = `${startX - 20}px`;
    floating.style.top = `${startY - 20}px`;
    document.body.append(floating);

    const endX = targetRect.left + (targetRect.width / 2);
    const endY = targetRect.top + (targetRect.height / 2);
    const animation = floating.animate([
      { transform: 'translate3d(0, 0, 0) scale(1)' },
      { transform: `translate3d(${endX - startX}px, ${endY - startY}px, 0) scale(.65)` },
    ], {
      duration: 650,
      easing: 'cubic-bezier(.65, 0, .35, 1)',
      fill: 'forwards',
    });
    const removeFloating = () => floating.remove();
    animation.addEventListener('finish', removeFloating, { once: true });
    animation.addEventListener('cancel', removeFloating, { once: true });
  };

  const initializeCartInteractions = () => {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('.product-form__btn-add-to-cart, .rwady-product-card__cart-button');
      if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
      playAnimation(button, [
        { transform: 'scale(1)' },
        { transform: 'scale(.88)', offset: 0.5 },
        { transform: 'scale(1)' },
      ], { duration: 180, easing: 'ease-out' });
    }, true);

    document.addEventListener('rwady:cart-add-success', (event) => animateProductToCart(event.detail));
    document.addEventListener(CART_UPDATED_EVENT, () => {
      requestCartState();
      pulseElements('[data-rwady-cart-trigger]', 1.18);
    });
    window.addEventListener('pageshow', requestCartState);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') requestCartState();
    });
    requestCartState();
  };

  const initializeProductShare = () => {
    document.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-rwady-product-share]');
      if (!button) return;
      const shareData = { title: button.dataset.shareTitle || document.title, url: button.dataset.shareUrl || window.location.href };
      try {
        if (navigator.share) await navigator.share(shareData);
        else await navigator.clipboard.writeText(shareData.url);
      } catch (error) {
        if (error?.name !== 'AbortError') window.location.href = `mailto:?subject=${encodeURIComponent(shareData.title)}&body=${encodeURIComponent(shareData.url)}`;
      }
    });
  };

  const initializeMobileProductPurchase = () => {
    document.addEventListener('change', (event) => {
      const checkbox = event.target.closest('[data-rwady-terms-toggle]');
      if (!checkbox) return;
      const footer = checkbox.closest('.rwady-mobile-product-purchase-footer');
      const buyNowButton = footer?.querySelector('.rwady-mobile-product-buy-now');
      if (buyNowButton) buyNowButton.disabled = !checkbox.checked;
    });

    document.addEventListener('click', (event) => {
      const buyNowButton = event.target.closest('.rwady-mobile-product-buy-now');
      if (!buyNowButton || buyNowButton.disabled) return;
      event.preventDefault();
      const content = buyNowButton.closest('.product-template__content__container');
      const addToCart = content?.querySelector('.product-form__btn-add-to-cart');
      if (!addToCart) return;
      document.addEventListener('rwady:cart-add-success', () => {
        window.location.assign(`${window.Shopify.routes.root}pages/checkout-information`);
      }, { once: true });
      addToCart.click();
    });
  };

  const initializeDesktopCategoriesMenu = () => {
    if (window.innerWidth < 1024) return;

    document.querySelectorAll('.rwady-header-categories').forEach((menu) => {
      if (menu.dataset.outsideCloseReady === 'true') return;
      menu.dataset.outsideCloseReady = 'true';
      document.addEventListener('click', (event) => {
        if (!menu.contains(event.target)) menu.removeAttribute('open');
      });
      menu.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          menu.removeAttribute('open');
          menu.querySelector(':scope > summary')?.focus();
        }
      });
      menu.addEventListener('click', (event) => {
        const targetButton = event.target.closest('[data-category-target]');
        if (!targetButton || !menu.contains(targetButton)) return;
        const targetPanel = menu.querySelector(`[data-category-panel="${CSS.escape(targetButton.dataset.categoryTarget)}"]`);
        if (!targetPanel) return;

        menu.querySelectorAll('[data-category-panel]').forEach((panel) => {
          const isTarget = panel === targetPanel;
          panel.hidden = !isTarget;
          panel.classList.toggle('is-active', isTarget);
        });
        menu.querySelector('.rwady-header-categories__panel')?.scrollTo({ top: 0 });
        targetPanel.querySelector('a, button')?.focus({ preventScroll: true });
      });
      menu.addEventListener('toggle', () => {
        if (menu.open) return;
        menu.querySelectorAll('[data-category-panel]').forEach((panel) => {
          const isRoot = panel.dataset.categoryPanel === 'root';
          panel.hidden = !isRoot;
          panel.classList.toggle('is-active', isRoot);
        });
      });
    });

    if (document.documentElement.dataset.rwadyCategoriesClickReady !== 'true') {
      document.documentElement.dataset.rwadyCategoriesClickReady = 'true';
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('#DrawerMenuDesktopPosition .button-category, #DrawerMenuDesktopPosition .rwady-all-categories-fallback');
        if (!trigger) return;

        const container = trigger.closest('#DrawerMenuDesktopPosition');
        const destination = container?.dataset.rwadyAllCategoriesUrl;
        if (!destination) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        window.location.assign(destination);
      }, true);
    }

    const mountMenu = () => {
      const destination = document.getElementById('DrawerMenuDesktopPosition');
      const source = document.getElementById('DrawerMenuDesktop');
      if (!destination || destination.querySelector('.drawer-menu') || !source || !window.Alpine) return;

      const helper = Alpine.store('xHelper');
      if (!helper?.setPosition) return;
      helper.setPosition('#DrawerMenuDesktopPosition', '#DrawerMenuDesktop');
      Alpine.store('xHeaderMenu')._drawerMenuEl = document.getElementById('DrawerMenu');
    };

    mountMenu();
    [100, 350, 900].forEach((delay) => window.setTimeout(mountMenu, delay));
  };


  const initializeProductListReturn = () => {
    if (window.RwadyListReturn) return;
    const key = 'rwady-product-list-return-v1';
    const pageKey = () => location.pathname + location.search;
    const productKey = (value) => {
      try { return new URL(value, location.href).pathname.match(/\/products\/([^/]+)/)?.[1] || ''; }
      catch (_) { return ''; }
    };
    const horizontalParents = (anchor) => {
      const parents = [];
      for (let el = anchor?.parentElement; el && el !== document.body; el = el.parentElement) {
        if (el.scrollWidth > el.clientWidth && /auto|scroll/.test(getComputedStyle(el).overflowX)) parents.push(el);
      }
      return parents;
    };
    const read = () => {
      try { return JSON.parse(sessionStorage.getItem(key) || 'null'); }
      catch (_) { return null; }
    };
    const write = (state) => {
      try { sessionStorage.setItem(key, JSON.stringify(state)); } catch (_) {}
    };
    let pages = [];
    let replaying = false;
    let restoreToken = 0;
    const valid = (state) => state && state.version === 1 && Date.now() - state.time < 1800000
      && typeof state.source === 'string' && state.source.startsWith('/') && !state.source.startsWith('//');
    const findAnchor = (state) => Array.from(document.querySelectorAll('main a[href]'))
      .find((a) => productKey(a.href) === state.product && a.getClientRects().length);
    document.addEventListener('rwady:list-page-loaded', (event) => {
      if (replaying) return;
      const detail = event.detail;
      if (!detail?.gridId || !detail?.url) return;
      const url = new URL(detail.url, location.href);
      if (url.origin !== location.origin) return;
      if (detail.replace) pages = pages.filter((item) => item.gridId !== detail.gridId);
      if (!pages.some((item) => item.url === url.href && item.gridId === detail.gridId)) {
        pages.push({ url: url.href, gridId: detail.gridId, source: pageKey(), replace: !!detail.replace });
      }
    });
    document.addEventListener('click', (event) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const back = event.target.closest('.rwady-mobile-product-hero-bar__back');
      if (back) {
        const state = read();
        event.preventDefault();
        event.stopImmediatePropagation();
        let fromList = false;
        try { const ref = new URL(document.referrer); fromList = ref.origin === location.origin && ref.pathname + ref.search === state?.source; } catch (_) {}
        if (valid(state) && state.product === productKey(location.href) && fromList) {
          write({ ...state, pending: true });
          if (history.length > 1) history.back();
          else location.assign(state.source);
        } else {
          let previousPage = '';
          try {
            const referrer = new URL(document.referrer);
            if (referrer.origin === location.origin && referrer.href !== location.href) previousPage = referrer.href;
          } catch (_) {}
          if (previousPage && history.length > 1) history.back();
          else location.assign(previousPage || back.dataset.rwadyBackFallback || window.Shopify?.routes?.root || '/');
        }
        return;
      }
      const anchor = event.target.closest('main a[href]');
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download') || productKey(location.href)) return;
      const url = new URL(anchor.href, location.href);
      const product = productKey(url.href);
      if (url.origin !== location.origin || !product) return;
      const state = {
        version: 1, time: Date.now(), source: pageKey(), product,
        top: anchor.getBoundingClientRect().top, y: window.scrollY,
        horizontal: horizontalParents(anchor).map((el) => el.scrollLeft),
        pages: pages.filter((item) => item.source === pageKey()).slice(-50),
      };
      write(state);
      try { history.replaceState({ ...history.state, rwadyListReturn: state }, ''); } catch (_) {}
    }, true);
    const restore = async (persisted = false) => {
      const stored = read();
      const state = history.state?.rwadyListReturn || (stored?.pending ? stored : null);
      const backwards = performance.getEntriesByType('navigation')[0]?.type === 'back_forward';
      if (!valid(state) || state.source !== pageKey() || productKey(location.href)
        || (!persisted && !backwards && !stored?.pending)) return;
      if (stored?.pending) write({ ...stored, pending: false });
      pages = Array.isArray(state.pages) ? state.pages : [];
      if (persisted) return;
      const token = ++restoreToken;
      const deadline = Date.now() + 15000;
      const cancelled = () => token !== restoreToken || Date.now() > deadline || pageKey() !== state.source;
      const cancel = () => { restoreToken += 1; };
      ['touchstart', 'wheel', 'keydown', 'pointerdown'].forEach((type) => window.addEventListener(type, cancel, { once: true, passive: true }));
      const pause = () => new Promise((resolve) => setTimeout(resolve, 100));
      replaying = true;
      try {
        while (!cancelled() && document.querySelector('#ProductGridContainer[hidden], [data-rwady-restore-pending]')) await pause();
        for (const item of pages) {
          if (cancelled()) break;
          const url = new URL(item.url, location.href);
          if (url.origin !== location.origin || item.source !== state.source) continue;
          const grid = document.getElementById(item.gridId);
          if (!grid) continue;
          const response = await fetch(url.href, { signal: AbortSignal.timeout(8000) });
          if (!response.ok) break;
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (cancelled()) break;
          const source = doc.getElementById(item.gridId);
          if (!source) continue;
          const existing = new Set(Array.from(grid.querySelectorAll('a[href]')).map((a) => productKey(a.href)).filter(Boolean));
          if (item.replace) grid.innerHTML = source.innerHTML;
          else Array.from(source.getElementsByClassName('grid-item')).forEach((card) => {
            const product = productKey(card.querySelector('a[href*="/products/"]')?.href || '');
            if (product && !existing.has(product)) { grid.insertAdjacentHTML('beforeend', card.innerHTML); existing.add(product); }
          });
          doc.querySelectorAll('[id^="btn-pagination-"]').forEach((next) => {
            const current = document.getElementById(next.id);
            if (current) current.innerHTML = next.innerHTML;
          });
        }
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
        let stable = 0;
        let previous = -1;
        while (!cancelled() && stable < 10) {
          const anchor = findAnchor(state);
          if (anchor) horizontalParents(anchor).forEach((el, index) => {
            if (Number.isFinite(state.horizontal?.[index])) el.scrollLeft = state.horizontal[index];
          });
          const target = anchor ? Math.max(0, window.scrollY + anchor.getBoundingClientRect().top - state.top) : state.y;
          window.scrollTo({ top: target, behavior: 'instant' });
          const height = document.documentElement.scrollHeight;
          stable = anchor && height === previous ? stable + 1 : 0;
          previous = height;
          await pause();
        }
      } catch (error) {
        if (!cancelled()) window.scrollTo({ top: state.y, behavior: 'instant' });
      } finally {
        replaying = false;
        ['touchstart', 'wheel', 'keydown', 'pointerdown'].forEach((type) => window.removeEventListener(type, cancel));
      }
    };
    window.RwadyListReturn = { restore };
    window.addEventListener('pageshow', (event) => restore(event.persisted));
    if (document.readyState === 'complete') restore();
  };

  initializeProductListReturn();

  window.RwadyInteractions = { captureAddSource, requestCartState, syncCartState };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeCategoryNameSearch();
    initializeSearchNormalization();
      initializeUnifiedCollectionSearch();
      initializeProductShare();
      initializeCartInteractions();
      initializeWishlist();
      initializeMobileProductPurchase();
      initializeDesktopCategoriesMenu();
    }, { once: true });
  } else {
    initializeCategoryNameSearch();
    initializeSearchNormalization();
    initializeUnifiedCollectionSearch();
    initializeProductShare();
    initializeCartInteractions();
    initializeWishlist();
    initializeMobileProductPurchase();
    initializeDesktopCategoriesMenu();
  }
})();



